import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axiosInstance from '../../utils/api';

export default function QuestionnaireSchema() {
  const [loading, setLoading] = useState(true);
  const [schema, setSchema] = useState({});
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/api/admin/dashboard/questionnaire-schema/');
        setSchema(response.data.sections || {});
      } catch (err) {
        console.error('Failed to load questionnaire schema:', err);
        setError(err.response?.data?.error || 'Failed to load questionnaire schema');
      } finally {
        setLoading(false);
      }
    };
    fetchSchema();
  }, []);

  const filteredSchema = useMemo(() => {
    if (!search.trim()) return schema;
    const query = search.trim().toLowerCase();
    const filtered = {};
    Object.entries(schema).forEach(([section, fields]) => {
      const hits = fields.filter((field) =>
        field.label.toLowerCase().includes(query)
      );
      if (hits.length > 0) {
        filtered[section] = hits;
      }
    });
    return filtered;
  }, [schema, search]);

  const totalQuestions = useMemo(() => {
    return Object.values(schema).reduce((sum, fields) => sum + fields.length, 0);
  }, [schema]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Questionnaire Questions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Read-only reference of questions by section.
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search questions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Sections: ${Object.keys(schema).length}`} />
            <Chip label={`Questions: ${totalQuestions}`} />
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        Object.entries(filteredSchema).map(([section, fields]) => (
          <Accordion key={section} defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                  {section.replace(/_/g, ' ')}
                </Typography>
                <Chip size="small" label={`${fields.length} questions`} />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Paper variant="outlined" sx={{ p: 1 }}>
                <List dense>
                  {fields.map((field, index) => (
                    <React.Fragment key={`${section}.${field.name}`}>
                      <ListItem>
                        <Tooltip title={field.name}>
                          <ListItemText
                            primary={`${index + 1}. ${field.label}`}
                          />
                        </Tooltip>
                      </ListItem>
                      {index < fields.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Container>
  );
}
